import React, { useEffect, useState, useTransition } from 'react';
import { MissingItem } from '../../sales-common/DynamicCols/MissingItem';
import { Routes } from '../../../../common';
import { ISalesWizardButtonMenu, SalesWizardButtonMenu } from './SalesWizardButtonMenu';
import { PageSteps } from '../../sales-common/NewHeader';
import { VehicleContext, isValidStockSaleOrder } from '../../../../utils';
import { MAKE, OrderCreationType } from 'next-common';
import { isEmpty } from 'lodash';
import { IPcodeValidation } from '..';
import { Button } from '@repo/ui/components';
import { cn } from 'types';
import { useUnifiedNavigate } from '@common/hooks/useUnifiedNavigation';
import { usePathname, useRouter } from 'next/navigation';

export const menuTitles = {
  Vehicle: 'Konfigurer bil',
  VehicleAccessories: 'Tilbehør',
  Finance: 'Finansiering og tjenester',
  Signing: 'Signering',
  ImporterOptions: 'Opsjoner fra importør',
};

export enum WizardSteps {
  Vehicle,
  VehicleAccessories,
  Finance,
  Signing,
}

export enum VarebilWizardSteps {
  Vehicle,
  ImporterOptions,
  VehicleAccessories,
  Finance,
  Signing,
}

export interface ISalesWizardMenuProps extends ISalesWizardButtonMenu {
  rootUrl: string;
  updateStep: (step: number) => void;
  hasSelectedVehicle: boolean;
  disablePCodePage?: boolean;
  invalidPcodesValidation?: IPcodeValidation;
  onCustomClick?(): void;
}

export interface IStepMenuData {
  activeStep: number;
  route: string;
  routeIsActive: boolean;
  routeIsActiveClass: string;
  routeName: string;
}

export const getStepsKeys = (isVarebilEnabled: boolean): string[] => {
  return Object.keys(isVarebilEnabled ? VarebilWizardSteps : WizardSteps).filter((key) =>
    Number.isNaN(parseInt(key, 10))
  );
};

const getStepRoute = (step: string, orderId = '', rootUrl = '') => {
  switch (step) {
    case 'ImporterOptions':
      return Routes.getImporterOptionsPage(orderId, rootUrl);
    case 'VehicleAccessories':
      return Routes.getVehicleAccessoriesPage(orderId, rootUrl);
    case 'Finance':
      return Routes.getFinancePage(orderId, rootUrl);
    case 'Signing':
      return Routes.getSigningPage(orderId, rootUrl);
    case 'Vehicle':
    default:
      return Routes.getVehiclePickerPage(orderId, rootUrl);
  }
};

const isMenuItemActive = (currentPath: string, path: string) => currentPath?.includes(path);

export const getActiveStep = (
  rootUrl = '',
  isVarebilEnabled: boolean,
  pathname?: string
): WizardSteps | VarebilWizardSteps => {
  const currentPath = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const activeStepList = getStepsKeys(isVarebilEnabled)
    ?.map((key) => (isMenuItemActive(currentPath, getStepRoute(key, '', rootUrl)) ? key : null))
    ?.filter((item) => !!item);
  return isVarebilEnabled ? VarebilWizardSteps[activeStepList[0]] : WizardSteps[activeStepList[0]];
};

export const getStepData = (
  activeStep: number,
  orderId: string = '',
  rootUrl: string = '',
  isVarebilEnabled: boolean
) => {
  return getStepsKeys(isVarebilEnabled)?.map?.((key, index) => {
    const routeIsActive = index === activeStep;
    const routeIsActiveClass = routeIsActive ? 'active' : 'inactive';
    return {
      activeStep,
      route: getStepRoute(key, orderId, rootUrl),
      routeIsActive,
      routeIsActiveClass,
      routeName: key,
    };
  });
};

// Manage order step
export const disabledOrderStep = (orderType, isChange, isFactoryOrder) => {
  switch (orderType) {
    case OrderCreationType.STOCK:
      if (isChange && isFactoryOrder) {
        return {
          disabledSteps: [
            WizardSteps[WizardSteps.VehicleAccessories],
            WizardSteps[WizardSteps.Finance],
            WizardSteps[WizardSteps.Signing],
          ],
        };
      } else {
        return {
          disabledSteps: [WizardSteps[WizardSteps.VehicleAccessories], WizardSteps[WizardSteps.Finance]],
        };
      }
    case OrderCreationType.DEMO:
      if (isChange && isFactoryOrder) {
        return {
          disabledSteps: [WizardSteps[WizardSteps.Finance], WizardSteps[WizardSteps.Signing]],
        };
      } else {
        return {
          disabledSteps: [WizardSteps[WizardSteps.Finance]],
        };
      }
    default:
      return {
        disabledSteps: [],
      };
  }
};

export const SalesWizardMenu = (props: ISalesWizardMenuProps) => {
  const pathname = usePathname();
  const activeStep = getActiveStep(props.rootUrl, props?.isVarebilEnabled, pathname);
  const stepData = getStepData(activeStep, props.orderId, props.rootUrl, props?.isVarebilEnabled);
  const [missingItems, setMissingItems] = React.useState<string[]>([]);
  const [isButtonDisable, setIsButtonDisable] = useState<boolean>(false);
  const [isPending, startTransition] = useTransition();
  const navigate = useUnifiedNavigate();
  const router = useRouter();
  const { getVehicleData } = React.useContext(VehicleContext) ?? {};
  const isStockSaleOrder = isValidStockSaleOrder(props.order, props?.configuration);
  const { disabledSteps } = disabledOrderStep(props?.order?.lead?.orderType, props?.isChange, props?.isFactoryOrder);

  useEffect(() => {
    const items: string[] = [];
    if (!props.order?.customer?.bosCustomer) items?.push(MissingItem.Customer);
    if (!props.order?.vehicles || props.order?.vehicles?.length < 1) items?.push(MissingItem.Car);
    if (!props.calculationId && !props?.order?.chosenOffer?.id) items?.push(MissingItem.Offer);
    setMissingItems(items);
  }, [props?.order, props?.calculationId]);

  useEffect(() => {
    props.updateStep(activeStep);
  }, [activeStep]);

  useEffect(() => {
    setIsButtonDisable(props?.menuLoading);
  }, [props?.menuLoading]);

  useEffect(() => {
    if (isPending) {
      setIsButtonDisable(true);
      return;
    }
    if (!props?.menuLoading) {
      setIsButtonDisable(false);
    }
  }, [isPending, props?.menuLoading]);

  useEffect(() => {
    stepData?.forEach((step) => {
      if (step?.route) {
        router.prefetch(step.route);
      }
    });
  }, [router, stepData]);

  const isClickableLink = (index: number) => {
    return index === activeStep ? true : !props?.menuLoading;
  };

  const isSigningClickable = (index: number): boolean => {
    if (props.isVarebilEnabled && index !== VarebilWizardSteps.Signing) {
      return true;
    } else if (!props.isVarebilEnabled && index !== WizardSteps.Signing) {
      return true;
    } else if (props?.isUsedCar && !missingItems?.length) {
      return true;
    }
    return !missingItems?.length && !props?.invalidPcodesValidation?.isInvalidPcodes;
  };

  const isStepClickable = (index: number): boolean => {
    return (
      !!props.orderId &&
      props.hasSelectedVehicle &&
      isClickableLink(index) &&
      !props?.isUnavailablePimData &&
      isSigningClickable(index)
    );
  };

  const handleMenuClick = (stepdata: IStepMenuData) => {
    setIsButtonDisable(true);

    startTransition(() => {
      if (
        activeStep === WizardSteps?.['Vehicle'] ||
        activeStep === VarebilWizardSteps?.['ImporterOptions'] ||
        activeStep === WizardSteps?.['VehicleAccessories'] ||
        activeStep === WizardSteps?.['Finance'] ||
        (props?.isVarebilEnabled ? activeStep === VarebilWizardSteps?.['Finance'] : false)
      ) {
        props?.onClick(stepdata);
      } else {
        navigate(stepdata?.route);
        props?.onCustomClick();
      }
    });
  };

  const isSelectedVehicleAvailable = () => {
    const isValidForeignCar = props.isForeignBrand && !isEmpty(props.order?.vehicles?.[0]?.serialNo);
    const isValidUsedCar = props.isUsedCar && !isEmpty(props.order?.vehicles?.[0]?.serialNo);
    if (
      props?.order?.externalPricing?.make === MAKE.Mercedes ||
      isStockSaleOrder ||
      isValidForeignCar ||
      isValidUsedCar
    ) {
      return true;
    }
    if (props?.orderId) {
      return getVehicleData()?.modelId && getVehicleData()?.variantId;
    }
    return false;
  };

  const getCommonDisabledCondition = (key) => {
    return (
      (key === 'ImporterOptions' && props.disablePCodePage) ||
      props?.isUnavailablePimData ||
      isButtonDisable ||
      (key !== WizardSteps[WizardSteps.Vehicle] &&
        props.activeStep === PageSteps.START &&
        !isSelectedVehicleAvailable())
    );
  };

  const editOrderDisableKey = (key) => {
    if (props.order?.isEditOrder && key === WizardSteps[WizardSteps.Vehicle]) {
      return true;
    }
  };
  const getDisabledOrderStep = (key) => {
    return getCommonDisabledCondition(key) || disabledSteps.includes(key) || editOrderDisableKey(key);
  };

  const getSigningDisable = (key, index) => {
    return (
      getCommonDisabledCondition(key) ||
      !isSigningClickable(index) ||
      disabledSteps.includes(key) ||
      editOrderDisableKey(key)
    );
  };

  const getStepName = (key: string, make: string) => {
    if (key === 'Vehicle' && props?.isMerBrand && !props.isStockSaleOrder) {
      return 'Konfigurer eller velg bil';
    } else if (key === 'Vehicle' && (props.isUsedCar || props.isForeignBrand)) {
      return 'Velg bil';
    } else return menuTitles?.[key] ?? '';
  };

  return (
    <div className="flex items-center justify-between sticky top-0 w-full h-[4.5rem] px-12 text-center bg-lightGrey border-b border-b-neutralGrey">
      <div className="flex gap-4">
        {getStepsKeys(props?.isVarebilEnabled)?.map((key, index) => {
          const isActive = index === activeStep;
          const disabled = props?.order
            ? isActive
              ? false
              : isStepClickable(index)
                ? getDisabledOrderStep(key)
                : getSigningDisable(key, index)
            : true;

          return (
            <Button
              key={key}
              className={cn(
                'h-9 border py-2 px-4',
                'enabled:hover:bg-darkBlue enabled:hover:text-white',
                'disabled:border-neutralGrey disabled:text-neutralPlus disabled:bg-white disabled:opacity-100',
                stepData?.[index]?.routeIsActiveClass === 'active' ? '' : 'bg-white'
              )}
              appearance={stepData?.[index]?.routeIsActiveClass === 'active' ? 'solid' : 'outline'}
              variant="purple"
              onClick={() => handleMenuClick(stepData?.[index])}
              disabled={disabled}
            >
              <span className="text-sm/[100%] font-semibold">
                {getStepName(key, props?.order?.externalPricing?.make)}
              </span>
            </Button>
          );
        })}
      </div>
      <SalesWizardButtonMenu
        isUsedCar={props.isUsedCar}
        orderId={props?.orderId}
        order={props?.order}
        calculationId={props?.calculationId}
        activeStep={props?.activeStep}
        isUnavailablePimData={props?.isUnavailablePimData}
        menuLoading={props?.menuLoading}
        onClickSigning={props?.onClickSigning}
        onClick={props?.onClick}
        isStockSaleOrder={isStockSaleOrder}
        stepData={stepData?.[stepData?.length - 1]}
        allStepData={stepData}
        isChange={props.isChange}
        isFactoryOrder={props.isFactoryOrder}
        isVarebilEnabled={props?.isVarebilEnabled}
        disablePCodePage={props.disablePCodePage}
        invalidPcodesValidation={props?.invalidPcodesValidation}
        isForeignBrand={props.isForeignBrand}
      />
    </div>
  );
};
